import { useCallback, useEffect, useState } from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import BomComparisonView from '../bomLibrary/BomComparisonView'
import { getWorkflowBomUpdateImpact, readBomLibraryError } from '../bomLibrary/bomLibraryApi'

/**
 * BomUpdateImpactDialog — S135 (HU-07/CA7.3, RN-07.5, RN-07.6): re-points the workflow at a newer
 * version of the library BOM it is linked to, but never before showing what that costs.
 *
 * The structural comparison (what the two versions differ in) is only half the story; the other half
 * is how it lands on THIS workflow's assignments, which is what the three blocks of N19 answer:
 * components that disappear although a job consumed them, components that arrive with no assignment,
 * and components whose quantity moved.
 *
 * Corrección PO 2026-09-10: confirming WRITES NOTHING. In this editor everything is entered in the
 * interface and persisted by the general "Save", and moving to another version of the linked BOM is
 * no exception: the choice is reported through `onUpdateSelected` and becomes a pending change of
 * the workflow (`pendingBomLink` with `mode: 'UPDATE'`), so "Revert" undoes it and the save is what
 * calls N20. The assignments N20 retires are therefore reported by that save, not here.
 *
 * The new components show up as unassigned in the BOM section for the plainest of reasons: nothing
 * points at them yet. No extra marking is needed for that (S135).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {number} props.workflowId
 * @param {number} props.targetBomId the version to move to (the highest APPROVED one on offer)
 * @param {function(object)} props.onUpdateSelected called with the `BomUpdateImpact` the user
 * accepted, so the editor records the update as a pending change of the workflow
 * @param {function()} props.onCancel
 */
const BomUpdateImpactDialog = ({ open = false, workflowId = 0, targetBomId = 0, onUpdateSelected = () => {}, onCancel = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [impact, setImpact] = useState({})
  const [errorMessage, setErrorMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const readImpact = useCallback(async () => {
    if (!open || !workflowId || !targetBomId) {
      return
    }
    setBusy(true)
    setErrorMessage('')
    try {
      setImpact(await getWorkflowBomUpdateImpact(workflowId, targetBomId) || {})
    } catch (thrown) {
      setErrorMessage(readBomLibraryError(thrown).error)
    }
    setBusy(false)
  }, [open, workflowId, targetBomId])

  useEffect(() => {
    readImpact()
  }, [readImpact])

  const removedAssigned = impact.removedAssigned || []
  const newUnassigned = impact.newUnassigned || []
  const quantityChanged = impact.quantityChanged || []

  // The user has seen the impact and accepts it: from here on it is a pending change of the
  // workflow like any other, and the editor is the one that owns it.
  const selectUpdate = () => {
    onUpdateSelected(impact)
    onCancel()
  }

  return (
    <SmartDialog
      setOpen={open}
      title={{ render: <Typography variant='h6'>{t_('Update to a newer BOM version')}</Typography> }}
      acceptLabel='Update'
      acceptCallback={selectUpdate}
      cancelCallback={onCancel}
      // Confirming an impact the user has not seen yet (N19 still in flight) is exactly what this
      // dialog exists to prevent, so the buttons wait for it.
      disableButtons={busy}
      autoClose={false}
      paperWidth='60rem'
      renderComponent={
        <Stack spacing={2} sx={{ textAlign: 'left' }}>
          <BomComparisonView comparison={impact.comparison} />
          {removedAssigned.length > 0
            ? (
              <>
                <Divider />
                <ComponentJobsBlock
                  testId='bom-impact-removed-assigned'
                  title={t_('Removed components with assignments')}
                  items={removedAssigned}
                  t_={t_}
                />
              </>
              )
            : null}
          {newUnassigned.length > 0
            ? (
              <>
                <Divider />
                <Stack spacing={1} data-testid='bom-impact-new-unassigned'>
                  <Typography variant='subtitle2'>{t_('New components (unassigned)')}</Typography>
                  {newUnassigned.map((item) => (
                    <Typography key={item.componentKey} variant='body2'>
                      {item.resourceCode} {item.resourceName} · {item.position} · {t_('Quantity')} {item.quantity}
                    </Typography>
                  ))}
                </Stack>
              </>
              )
            : null}
          {quantityChanged.length > 0
            ? (
              <>
                <Divider />
                <Stack spacing={1} data-testid='bom-impact-quantity-changed'>
                  <Typography variant='subtitle2'>{t_('Quantity changes')}</Typography>
                  {quantityChanged.map((item) => (
                    <Stack key={item.component?.componentKey} spacing={0.5}>
                      <Typography variant='body2'>
                        {item.component?.resourceCode} · {item.oldQuantity} → {item.newQuantity}
                      </Typography>
                      <JobList jobs={item.jobs} t_={t_} />
                    </Stack>
                  ))}
                </Stack>
              </>
              )
            : null}
          {errorMessage ? <Typography color='error.main' data-testid='bom-impact-error'>{t_(errorMessage)}</Typography> : null}
        </Stack>
      }
    />
  )
}

/** A list of `ImpactedComponent` ({component, jobs[]}), the shape shared by two of the blocks. */
const ComponentJobsBlock = ({ testId, title, items, t_ }) => (
  <Stack spacing={1} data-testid={testId}>
    <Typography variant='subtitle2'>{title}</Typography>
    {items.map((item) => (
      <Stack key={item.component?.componentKey} spacing={0.5}>
        <Typography variant='body2'>
          {item.component?.resourceCode} {item.component?.resourceName} · {item.component?.position}
        </Typography>
        <JobList jobs={item.jobs} t_={t_} />
      </Stack>
    ))}
  </Stack>
)

const JobList = ({ jobs = [], t_ }) => (
  <Typography variant='caption'>
    {jobs.map((job) => job.jobCode + ' (' + t_('Quantity') + ' ' + job.quantity + ')').join(', ')}
  </Typography>
)

export default BomUpdateImpactDialog
